import Table from "@/common/components/Table";
import TextField from "@/common/components/TextField";
import { useBillingEvents } from "@/common/hooks/queries/useBillingEvents";
import useDebounce from "@/common/hooks/useDebounce";
import { Mapper } from "@/common/services/mapper";
import { BillingEventData } from "@/common/types/billing/event.type";
import { ArrowBigRightIcon, SearchIcon } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import { useFilterDate } from "@/common/hooks/useFilterDate";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { EstablishmentBillingEventTransformer } from "@/features/establishments/services/establishmentBillingEventTransformer";
import { EstablishmentBillingEventTableItem } from "@/features/establishments/types/establishmentEvents.type";
import { defaultColumns } from "./columns";
import Filters from "./components/Filters";
import { internalBaseEquipments } from "@/common/constants/internalBaseEquipments";
import { useEffect, useMemo } from "react";
import Button from "@/common/components/Button";
import QueryString from "qs";
import useUserPermissions from "@/common/hooks/useUserPermissions";

interface EventsProps {
  establishmentDisplayName?: string
}

const Events = ({ establishmentDisplayName }: EventsProps) => {
  const { id: establishmentCompanyId } = useParams();

  const { filterParams, setFilterParams } = useFilterParams({
    fromInternalBase: internalBaseEquipments.find(({ value }) => value === 'false')
  });

  const { date } = useFilterDate(filterParams);

  const { hasUserPermissionWithProperty, isUserPermissionsPending } = useUserPermissions();

  const { formattedDate } = useFilterDate(filterParams);

  const [search, setSearch, instantSearch, setInstantSearch] = useDebounce(filterParams.search || '');

  const reportUrl = QueryString.stringify({
    generator_klass: 'Reports::Admin::EstablishmentConsumptionsTotalizerReport',
    q: {
      establishment_company_id_eq: {
        label: establishmentDisplayName,
        value: establishmentCompanyId,
      },
      created_at_gteq: date.from?.toISOString(),
      created_at_lteq: date.to?.toISOString(),
    },
  });

  const {
    data: companyBillingEvents,
    meta,
    isFetching: companyBillingEventsIsFetching,
    error: companyBillingEventsError = {} as any,
    refetch: refetchCompanyBillingEvents,
  } = useBillingEvents({
    filters: {
      search,
      page: filterParams.page || 1,
      fromInternalBase: filterParams.fromInternalBase?.value,
      establishmentCompanyId: establishmentCompanyId || '',
      createdAtStartDate: formattedDate.from,
      createdAtEndDate: formattedDate.to,
      productId: filterParams.product?.value,
      isRefunded: filterParams.isRefunded?.value,
    },
    mapper: Mapper.mapWithTransformer<BillingEventData[], EstablishmentBillingEventTableItem[]>(
      { transformer: EstablishmentBillingEventTransformer.tableTransformer() }
    )
  });

  useEffect(() => {
    if (filterParams.search !== instantSearch)
      setInstantSearch(filterParams.search);
  }, [filterParams.search]);

  const hasPermissionToCreateReport = useMemo(() => {
    return hasUserPermissionWithProperty({
      operationKlass: 'Admin::Reports::Entries::Create',
      propertyKey: 'generator_klass',
      propertyValue: 'Reports::Admin::EstablishmentConsumptionsTotalizerReport',
    });
  }, [isUserPermissionsPending]);

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-192px)]"
      statusCode={companyBillingEventsError?.status}
      refetchFunction={refetchCompanyBillingEvents}
    >
      <div className="flex flex-col gap-6 h-[calc(100vh-192px)]">
        <TextField
          LeftIcon={<SearchIcon />}
          placeholder="Pesquisar"
          className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
          onChange={(e) => {
            setSearch((e.target as HTMLInputElement).value);
            setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value }));
          }}
          value={filterParams.search}
        />

        <div className="flex justify-between">
          <Filters date={date} />

          <Link to={`/reports/create?${reportUrl}`}>
            <Button
              className="py-2 px-4"
              disabled={!hasPermissionToCreateReport}
              tooltipMessage={!hasPermissionToCreateReport ? 'Você não tem permissão' : undefined}
            >
              Exportar Totalizadores

              <ArrowBigRightIcon size={18} />
            </Button>
          </Link>
        </div>

        <Table
          columns={defaultColumns}
          isLoadingData={companyBillingEventsIsFetching}
          page={filterParams.page || 1}
          rows={companyBillingEvents}
          totalPages={meta.total_pages}
          totalCount={meta.total}
          hasPagination
          rowEmptyValue="--"
          setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
        />
      </div>
    </ErrorMessageWrapper>
  );
};

export default Events;
